export type NonOmsOptionView = {
  name: string
  values: string[]
}

export type NonOmsVariantView = {
  id: string
  name: string
  optionLabel: string
  salePrice: string
  costPrice: string
  available: string
  sku: string
  barcode: string
  ean: string
  imageUrl: string
  isTracked: boolean
  weight: string
  weightUnit: string
  length: string
  width: string
  height: string
  dimensionUnit: string
}

export type NonOmsProductView = {
  name: string
  shortDescription: string
  description: string
  sku: string
  barcode: string
  ean: string
  hasVariants: boolean
  createdAt: string
  updatedAt: string
  options: NonOmsOptionView[]
  variants: NonOmsVariantView[]
  images: string[]
  category: string
  type: string
  vendor: string
  tags: string[]
  status: string
  salePrice: string
  costPrice: string
  comparePrice: string
  weight: string
  weightUnit: string
  length: string
  width: string
  height: string
  dimensionUnit: string
}

const blankView = (): NonOmsProductView => ({
  name: '',
  shortDescription: '',
  description: '',
  sku: '',
  barcode: '',
  ean: '',
  hasVariants: false,
  createdAt: '',
  updatedAt: '',
  options: [],
  variants: [],
  images: [],
  category: '',
  type: '',
  vendor: '',
  tags: [],
  status: '',
  salePrice: '',
  costPrice: '',
  comparePrice: '',
  weight: '',
  weightUnit: '',
  length: '',
  width: '',
  height: '',
  dimensionUnit: '',
})

const asText = (value: unknown): string => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    if ('value' in record) return asText(record.value)
    if ('name' in record) return asText(record.name)
    if ('label' in record) return asText(record.label)
    if ('api_name' in record) return asText(record.api_name)
  }
  return ''
}

const firstText = (...values: unknown[]): string => {
  for (const value of values) {
    const text = asText(value).trim()
    if (text) return text
  }
  return ''
}

const unitText = (value: unknown, fallbackUnit?: unknown): string => {
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return firstText(record.unit, record.weight_unit, record.dimension_unit, fallbackUnit)
  }
  return firstText(fallbackUnit)
}

const optionValues = (variant: any): string[] =>
  [1, 2, 3]
    .map((index) => asText(variant?.[`platform_product_option_value${index}`]?.value).trim())
    .filter(Boolean)

const variantImageUrl = (variant: any): string => {
  const image = variant?.image
  if (image && typeof image === 'object') {
    return firstText(image.image_url, image.url, image.src)
  }
  return firstText(variant?.image_url, variant?.imageUrl)
}

const mapVariant = (variant: any, index: number): NonOmsVariantView => {
  const options = optionValues(variant)
  return {
    id: firstText(variant?.id, index),
    name: asText(variant?.name),
    optionLabel: options.join(' / '),
    salePrice: firstText(variant?.price, variant?.sale_price, variant?.salePrice),
    costPrice: firstText(variant?.cost_price, variant?.costPrice),
    available: firstText(variant?.quantity, variant?.total_available_quantity, variant?.available),
    sku: asText(variant?.sku),
    barcode: asText(variant?.barcode),
    ean: asText(variant?.ean),
    imageUrl: variantImageUrl(variant),
    isTracked: Boolean(variant?.isTracked),
    weight: firstText(variant?.weight),
    weightUnit: unitText(variant?.weight, variant?.weight_unit ?? variant?.weightUnit),
    length: firstText(variant?.length),
    width: firstText(variant?.width),
    height: firstText(variant?.height),
    dimensionUnit: firstText(variant?.dimension_unit, variant?.dimensionUnit),
  }
}

const mapOptions = (product: any): NonOmsOptionView[] => {
  const options = Array.isArray(product?.options) ? product.options : []
  return options
    .map((option: any) => {
      const values = Array.isArray(option?.option_values)
        ? option.option_values.map((item: any) => asText(item?.value ?? item).trim()).filter(Boolean)
        : []
      return {
        name: asText(option?.name).trim(),
        values,
      }
    })
    .filter((option: NonOmsOptionView) => option.name || option.values.length > 0)
}

const mapImages = (product: any): string[] => {
  const images = Array.isArray(product?.image_set) ? product.image_set : []
  const withUrl = images
    .map((image: any) => ({
      url: firstText(image?.image_url, image?.url, image?.src),
      active: image?.is_active !== false,
      position: Number(image?.image_position ?? 0),
    }))
    .filter((image: { url: string }) => Boolean(image.url))

  const active = withUrl.filter((image: { active: boolean }) => image.active)
  const source = active.length > 0 ? active : withUrl
  return source
    .sort((a: { position: number }, b: { position: number }) => a.position - b.position)
    .map((image: { url: string }) => image.url)
}

const mapTags = (product: any): string[] => {
  if (Array.isArray(product?.tags)) {
    return product.tags.map((tag: unknown) => asText(tag).trim()).filter(Boolean)
  }
  return asText(product?.tags)
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
}

export const mapPlatformProductToReadOnlyView = (product: any): NonOmsProductView => {
  if (!product || typeof product !== 'object') return blankView()

  const variants = (Array.isArray(product?.varients_data) ? product.varients_data : []).map(mapVariant)
  const options = mapOptions(product)
  const hasVariants = options.length > 0 || variants.length > 1
  const singleVariant = !hasVariants ? variants[0] : undefined

  return {
    name: asText(product?.name),
    shortDescription: firstText(product?.short_description, product?.genralDescription, product?.subtitle),
    description: asText(product?.description),
    sku: singleVariant?.sku ?? '',
    barcode: singleVariant?.barcode ?? '',
    ean: !hasVariants ? firstText(product?.varients_data?.[0]?.ean, product?.ean) : '',
    hasVariants,
    createdAt: firstText(product?.api_created_at, product?.created_at),
    updatedAt: firstText(product?.updated_at, product?.api_updated_at),
    options,
    variants,
    images: mapImages(product),
    category: firstText(product?.category, product?.category_name),
    type: firstText(product?.type, product?.product_type),
    vendor: firstText(product?.vendor, product?.vendor_name),
    tags: mapTags(product),
    status: firstText(product?.api_status, product?.status),
    salePrice: firstText(product?.sale_price, product?.salePrice, singleVariant?.salePrice),
    costPrice: firstText(product?.cost_price, product?.costPrice, singleVariant?.costPrice),
    comparePrice: firstText(product?.compare_price, product?.comparePrice, product?.compare_at_price),
    weight: firstText(product?.weight, singleVariant?.weight),
    weightUnit: firstText(unitText(product?.weight, product?.weight_unit ?? product?.weightUnit), singleVariant?.weightUnit),
    length: firstText(product?.length, singleVariant?.length),
    width: firstText(product?.width, singleVariant?.width),
    height: firstText(product?.height, singleVariant?.height),
    dimensionUnit: firstText(product?.dimension_unit, product?.dimensionUnit, singleVariant?.dimensionUnit),
  }
}
